const VERSION_META_NAME = "insights-app-version";
export const APP_VERSION_PLACEHOLDER = "__INSIGHTS_APP_VERSION__";
const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000;
const DEFAULT_CONFIRMATION_DELAY_MS = 30 * 1000;
const VERSION_PATTERN = /^[A-Za-z0-9._-]{8,128}$/;

export interface AppVersionWatcherOptions {
  pollIntervalMs?: number;
  confirmationDelayMs?: number;
}

export function isValidAppVersion(version: string): boolean {
  return VERSION_PATTERN.test(version);
}

export function getLoadedAppVersion(): string | null {
  const version = document
    .querySelector<HTMLMetaElement>(`meta[name="${VERSION_META_NAME}"]`)
    ?.content.trim();

  if (
    version === undefined ||
    version === APP_VERSION_PLACEHOLDER ||
    !isValidAppVersion(version)
  ) {
    return null;
  }

  return version;
}

export async function fetchDeployedAppVersion(
  signal?: AbortSignal,
): Promise<string | null> {
  const query = new URLSearchParams({ versionCheck: Date.now().toString() });

  try {
    const response = await fetch(`/version?${query.toString()}`, {
      cache: "no-store",
      headers: { Accept: "text/plain" },
      signal: signal ?? null,
    });

    if (!response.ok) {
      return null;
    }

    const version = (await response.text()).trim();

    return isValidAppVersion(version) ? version : null;
  } catch {
    return null;
  }
}

export function watchAppVersion(
  onVersionChanged: (version: string) => void,
  options: AppVersionWatcherOptions = {},
): () => void {
  const loadedVersion = getLoadedAppVersion();
  if (loadedVersion === null) {
    return () => undefined;
  }

  const pollIntervalMs =
    options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const confirmationDelayMs =
    options.confirmationDelayMs ?? DEFAULT_CONFIRMATION_DELAY_MS;

  let stopped = false;
  let inFlight = false;
  let notified = false;
  let candidateVersion: string | null = null;
  let confirmationTimer: number | undefined;
  let pollTimer: number | undefined;
  let activeRequest: AbortController | undefined;

  const checkVersion = async () => {
    if (stopped || inFlight || notified) {
      return;
    }

    inFlight = true;
    activeRequest = new AbortController();
    const deployedVersion = await fetchDeployedAppVersion(
      activeRequest.signal,
    );
    activeRequest = undefined;
    inFlight = false;

    if (stopped || notified || deployedVersion === null) {
      return;
    }

    if (deployedVersion === loadedVersion) {
      candidateVersion = null;
      return;
    }

    if (candidateVersion === deployedVersion) {
      notified = true;
      if (confirmationTimer !== undefined) {
        window.clearTimeout(confirmationTimer);
      }
      if (pollTimer !== undefined) {
        window.clearInterval(pollTimer);
      }
      onVersionChanged(deployedVersion);
      return;
    }

    candidateVersion = deployedVersion;
    if (confirmationTimer !== undefined) {
      window.clearTimeout(confirmationTimer);
    }
    confirmationTimer = window.setTimeout(
      () => void checkVersion(),
      confirmationDelayMs,
    );
  };

  void checkVersion();
  pollTimer = window.setInterval(
    () => void checkVersion(),
    pollIntervalMs,
  );

  return () => {
    stopped = true;
    activeRequest?.abort();
    if (pollTimer !== undefined) {
      window.clearInterval(pollTimer);
    }
    if (confirmationTimer !== undefined) {
      window.clearTimeout(confirmationTimer);
    }
  };
}
